import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Send, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("grc_admin_token")}` });

export const PartnerActivationCard = () => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/partner-activation`, { headers: authHeader() })
      .then(({ data }) => setForm(data))
      .catch(() => setForm({ nexa_url: "", nexa_note: "", meeting_url: "", followup_enabled: false, followup_days: 3 }));
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/partner-activation`, form, { headers: authHeader() });
      setForm(data);
      toast.success("Activation settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that.");
    } finally {
      setBusy(false);
    }
  };

  const runFollowups = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/partner-activation/followups`, {}, { headers: authHeader() });
      const n = (data.sent || []).length;
      toast.success(n ? `Follow-up sent to ${n} waiting applicant${n === 1 ? "" : "s"}` : "No applicants were due a follow-up");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send those.");
    } finally {
      setSending(false);
    }
  };

  if (!form) {
    return (
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6 flex items-center gap-2 text-sm text-ink-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading activation settings…
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid="partner-activation-card">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-teal-light text-teal">
          <ShieldCheck size={17} />
        </span>
        <div>
          <h3 className="font-heading font-medium text-lg text-navy">Partner activation</h3>
          <p className="text-xs text-ink-500">
            The NEXA registration step, the optional intro call, and the follow-up for applicants still waiting.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-500">NEXA registration link</span>
          <input
            type="url"
            value={form.nexa_url || ""}
            onChange={set("nexa_url")}
            placeholder="https://…"
            data-testid="activation-nexa-url"
            className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
          />
          <span className="mt-1 block text-[11px] text-ink-400">
            Goes in the approval email and becomes step 1 of the first-week checklist. Until it's set, that step shows without a link.
          </span>
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-500">Partner Overview call link</span>
          <input
            type="url"
            value={form.meeting_url || ""}
            onChange={set("meeting_url")}
            placeholder="https://…"
            data-testid="activation-meeting-url"
            className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
          />
          <span className="mt-1 block text-[11px] text-ink-400">
            Offered to applicants while they wait. Never blocks access.
          </span>
        </label>
      </div>

      <label className="block mt-4">
        <span className="text-xs uppercase tracking-widest text-ink-500">What to tell them about NEXA</span>
        <textarea
          rows={3}
          value={form.nexa_note || ""}
          onChange={set("nexa_note")}
          placeholder="One short paragraph — what registering with NEXA is for and what they'll need to hand."
          data-testid="activation-nexa-note"
          className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
        />
      </label>

      <div className="mt-5 rounded-2xl bg-linen/60 border border-linen-border p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(form.followup_enabled)}
            onChange={set("followup_enabled")}
            data-testid="activation-followup-toggle"
            className="mt-0.5 w-4 h-4 accent-teal"
          />
          <span>
            <span className="text-sm font-semibold text-navy">Auto follow-up for waiting applicants</span>
            <span className="block text-[12px] text-ink-500 leading-relaxed">
              One email, once, to anyone still sitting in New or Under Review — so nobody wonders if
              they were forgotten. Includes their status link and the overview call.
            </span>
          </span>
        </label>
        <div className="mt-3 flex flex-wrap items-center gap-3 pl-7">
          <label className="flex items-center gap-2 text-xs text-ink-600">
            After
            <input
              type="number"
              min={1}
              max={30}
              value={form.followup_days ?? 3}
              onChange={set("followup_days")}
              data-testid="activation-followup-days"
              className="w-16 rounded-lg border border-linen-border p-1.5 text-sm text-navy bg-white text-center"
            />
            days
          </label>
          {form.last_sent_count ? (
            <span className="text-[11px] text-ink-400">Last sweep sent {form.last_sent_count}</span>
          ) : null}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          onClick={save}
          disabled={busy}
          data-testid="activation-save"
          className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save settings
        </button>
        <button
          onClick={runFollowups}
          disabled={sending}
          data-testid="activation-run-followups"
          className="inline-flex items-center gap-2 bg-white border border-linen-border hover:border-teal text-navy font-semibold text-sm px-5 py-2.5 rounded-full transition-colors disabled:opacity-60"
        >
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send follow-ups now
        </button>
      </div>
    </div>
  );
};

export default PartnerActivationCard;
